import { useHead } from '#imports';
import { useImageUtils } from '@/composables/useImageUtils';

const { getOgArticleImage } = useImageUtils();

const baseUrl = 'https://gravaa.com'; // Base URL of your site
const defaultImage = 'https://cdn.shopify.com/s/files/1/0901/6521/5606/files/Home-gravel_1200.jpg?v=1743542756'; // Default image URL
const defaultLogo = '/favicon.png'; // Default logo URL (served from public/)




// Default SEO & Schema Markup for all pages
export const useDefaultSeo = () => {
  useHead({
    title: 'Gravaa – Adaptable tire pressure',
    htmlAttrs: { lang: 'en' },
    meta: [
      { name: 'description', content: 'Experience the increased speed, comfort, and race-winning confidence of adaptable tire pressure technology.' },
      { property: 'og:title', content: 'Gravaa - Adaptable tire pressure' },
      { property: 'og:description', content: 'Experience the increased speed, comfort, and race-winning confidence of adaptable tire pressure technology.' },
      { property: 'og:image', content: defaultImage },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: baseUrl },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'Gravaa - Adaptable tire pressure' },
      { name: 'twitter:description', content: 'Experience the increased speed, comfort, and race-winning confidence of adaptable tire pressure technology.' },
      { name: 'twitter:image', content: defaultImage },
    ],
    link: [
      { rel: 'canonical', href: baseUrl },
      { rel: 'icon', href: defaultLogo },
    ],
    script: [
      // WebSite
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "url": baseUrl,
          "name": "Gravaa",
          "description": "Adaptable tire pressure technology",
          "inLanguage": "en",
          "publisher": {
            "@type": "Organization",
            "name": "Gravaa",
            "url": baseUrl,
            "logo": {
              "@type": "ImageObject",
              "url": defaultLogo,
              "width": 512,
              "height": 60
            }
          },
          "potentialAction": {
            "@type": "SearchAction",
            "target": {
              "@type": "EntryPoint",
              "urlTemplate": `${baseUrl}/search?q={search_term_string}`
            },
            "query-input": {
              "@type": "PropertyValueSpecification",
              "valueRequired": true,
              "valueName": "search_term_string"
            }
          }
        }),
      },
      // Organization
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Gravaa",
          "url": baseUrl,
          "logo": defaultLogo,
          "sameAs": [
            "https://www.instagram.com/gravaa_hub/",
            "https://www.linkedin.com/company/gravaabike/",
            "https://www.facebook.com/gravaa.hub",
            "https://www.youtube.com/@GravaaAdaptableTirePressure",
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+31 (0) 85 07 379 10",
            "email": "info@gravaa.com",
            "contactType": "Customer service",
            "areaServed": ["NL"], // Change to all? Or specific countries or add more? 
            "url": baseUrl,
          }
        }),
      },
    ],
  });
};


// Product SEO & Schema Markup
export const useProductSeo = (product: any) => {
  if (!product) return;

  const productUrl = `${baseUrl}/products/${product.handle}`;
  const productImage = product.featuredImage?.url
    ? new URL(product.featuredImage.url, baseUrl).href
    : `${defaultImage}`;

  useHead({
    title: product.title || 'Default Product Title',
    htmlAttrs: { lang: 'en' },
    
    link: [
      { rel: 'canonical', href: productUrl },
      { rel: 'icon', href: defaultLogo },
    ],
    script: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          "name": product.title || 'Gravaa Product',
          "category": product.productType || 'Gravaa Collection',
          "description": product.description || 'Gravaa Product Description',
          "image": [productImage],
          "url": productUrl,
          "sku": product.variants?.edges?.[0]?.node?.sku || 'N/A',
          "hasMeasurement": product.shortDescription.value || 'N/A',
          "brand": {
            "@type": "Brand",
            "name": product.brand || 'Gravaa',
          },
          "offers": {
            "@type": "Offer",
            "url": productUrl,
            "priceCurrency": product.priceRange?.maxVariantPrice?.currencyCode || 'EUR',
            "price": parseFloat(product.priceRange?.maxVariantPrice?.amount || 0).toFixed(2),
            "availability": product.availability
              ? `https://schema.org/${product.availability}`
              : 'https://schema.org/InStock',
            "itemCondition": "https://schema.org/NewCondition",
          },
        }),
      },
      // Breadcrumb schema at product level
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": {
                "@type": "WebPage",
                "@id": baseUrl,
              },
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": product.productType || 'Gravaa Collection',
              "item": {
                "@type": "WebPage",
                "@id": `${baseUrl}/shop/all`, // Change to collection URL if available
              },
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": product.title || 'Product',
              "item": {
                "@type": "WebPage",
                "@id": `${baseUrl}/products/${product.handle}`,
              },
            },
          ],
        }),
      },
    ],
  });
};

// Collection SEO & Schema Markup
export const useCollectionSeo = (collection: any) => {
  if (!collection) return;

  const collectionUrl = `${baseUrl}/shop/${collection.handle}`;
  const collectionImage = collection.image?.url
    ? new URL(collection.image.url, baseUrl).href
    : `${defaultImage}`;
  useHead({
    title: collection.title || 'Gravaa Collection',
    htmlAttrs: { lang: 'en' },
    meta: [
      { name: 'description', content: collection.description || 'Adaptable tire pressure technology.' },
      { property: 'og:title', content: collection.title || 'Gravaa - Adaptable tire pressure technology.' },
      { property: 'og:description', content: collection.description || 'Adaptable tire pressure technology.' },
      { property: 'og:image', content: collectionImage },
      { property: 'og:type', content: 'collection' },
      { property: 'og:url', content: collectionUrl },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: collection.title || 'Gravaa - Adaptable tire pressure technology.' },
      { name: 'twitter:description', content: collection.description || 'Adaptable tire pressure technology.' },
      { name: 'twitter:image', content: collectionImage },
    ],
    link: [
      { rel: 'canonical', href: collectionUrl },
      { rel: 'icon', href: defaultLogo },
    ],
    script: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": collection.title || 'Gravaa Collection',
          "description": collection.description || 'Adaptable tire pressure technology.',
          "url": collectionUrl,
          "image": [collectionImage],
          "publisher": {
            "@type": "Organization",
            "name": "Gravaa",
            "url": baseUrl,
            "logo": {
              "@type": "ImageObject",
              "url": defaultLogo,
              "width": 512,
              "height": 60
            }
          },
          "isPartOf": {
            "@type": "WebSite",
            "url": baseUrl,
            "name": "Gravaa"
          },
        }),
      },
      // Breadcrumb schema at collection level
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": {
                "@type": "WebPage",
                "@id": baseUrl,
              },
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": collection.title || 'Gravaa Collection',
              "item": {
                "@type": "WebPage",
                "@id": `${baseUrl}/shop/${collection.handle}`,
              },
            },
          ],
        }),
      },
    ],
  });
};


// Journal SEO & Schema Markup
export const useJournalSeo = (blog: any) => {

  const headerblog = blog.articles.find((article: any) => article.handle === 'journal-banner') || blog.articles[0];
  const image = getOgArticleImage(headerblog);
  
  console.log('Image for journal:', image);

  useHead({
    title: 'Journal - Gravaa',
    htmlAttrs: { lang: 'en' },
    meta: [
      { name: 'description', content: 'Explore cycling innovation, stories and insights on the Gravaa Journal.' },
      { property: 'og:title', content: 'Journal - Gravaa' },
      { property: 'og:description', content: 'Explore cycling innovation, stories and insights on the Gravaa Journal.' },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: `${baseUrl}/blogs/journal` },
      { property: 'og:image', content: image },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'Journal - Gravaa' },
      { name: 'twitter:description', content: 'Explore cycling innovation, stories and insights on the Gravaa Journal.' },
      { name: 'twitter:image', content: defaultImage },
    ],
    link: [
      { rel: 'canonical', href: `${baseUrl}/blogs/journal` },
      { rel: 'icon', href: defaultLogo },
    ],
    script: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Blog",
          "name": "Gravaa Journal",
          "description": "Explore cycling innovation, stories and insights on the Gravaa Journal.",
          "publishedAt": new Date().toISOString(),
          "url": `${baseUrl}/blogs/journal`,
          "publisher": {
            "@type": "Organization",
            "name": "Gravaa",
            "logo": {
              "@type": "ImageObject",
              "url": defaultLogo
            }
          }
        }),
      },
    ],
  });
};

// Journal Article SEO & Schema Markup
export const useArticleSeo = (article: any) => {
  if (!article) return;

  const articleUrl = `${baseUrl}/blog/journal/${article.handle}`;
  const articleImage = article.image
    ? new URL(article.image, baseUrl).href
    : `${defaultImage}`;
  

  useHead({
    title: article.title || 'Gravaa Journal',
    htmlAttrs: { lang: 'en' },
    meta: [
      { name: 'description', content: article.excerpt || 'Explore cycling innovation, stories and insights on the Gravaa Journal.' },
      { property: 'og:title', content: article.title || 'Gravaa Journal' },
      { property: 'og:description', content: article.excerpt || 'Explore cycling innovation, stories and insights on the Gravaa Journal.' },
      { property: 'og:image', content: articleImage || defaultImage },
      { property: 'og:type', content: 'article' },
      { property: 'og:url', content: articleUrl },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: article.title || 'Gravaa Journal' },
      { name: 'twitter:description', content: article.excerpt || 'Explore cycling innovation, stories and insights on the Gravaa Journal.' },
      { name: 'twitter:image', content: articleImage },
    ],
    link: [
      { rel: 'canonical', href: articleUrl },
      { rel: 'icon', href: defaultLogo },
    ],
    script: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          "headline": article.title || 'Gravaa Journal',
          "description": article.excerpt || 'Explore cycling innovation, stories and insights on the Gravaa Journal.',
          "image": [articleImage],
          "url": articleUrl,
          "datePublished": article.publishedAt || new Date().toISOString(),
          "author": {
            "@type": "Organization",
            "name": "Gravaa",
          },
        }),
      },
    ],
  });
};

// News SEO & Schema Markup
export const useNewsSeo = () => {

  useHead({
    title: 'News - Gravaa',
    htmlAttrs: { lang: 'en' },
    meta: [
      { name: 'description', content: 'Stay updated with the latest news and updates from Gravaa.' },
      { property: 'og:title', content: 'News - Gravaa' },
      { property: 'og:description', content: 'Stay updated with the latest news and updates from Gravaa.' },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: `${baseUrl}/news` },
      { property: 'og:image', content: defaultImage }, // Change?
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'News - Gravaa' },
      { name: 'twitter:description', content: 'Stay updated with the latest news and updates from Gravaa.' },
      { name: 'twitter:image', content: defaultImage }, // Change?
    ],
    link: [
      { rel: 'canonical', href: `${baseUrl}/news` },
      { rel: 'icon', href: defaultLogo },
    ],
    script: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "Gravaa News",
          "description": "Stay updated with the latest news and updates from Gravaa.",
          "url": `${baseUrl}/news`,
        }),
      },
    ],
  });
};

// Journal Article SEO & Schema Markup
export const useNewsArticleSeo = (article: any) => {
  if (!article) return;

  const articleUrl = `${baseUrl}/news/${article.handle}`;
  const articleImage = article.image
    ? new URL(article.image, baseUrl).href
    : `${defaultImage}`;

  useHead({
    title: article.title || 'Gravaa - News Article Title',
    htmlAttrs: { lang: 'en' },
    meta: [
      { name: 'description', content: article.excerpt || 'Stay updated with the latest news and updates from Gravaa' },
      { property: 'og:title', content: article.title || 'News - Gravaa' },
      { property: 'og:description', content: article.excerpt || 'Stay updated with the latest news and updates from Gravaa' },
      { property: 'og:image', content: articleImage || defaultImage },
      { property: 'og:type', content: 'news article' },
      { property: 'og:url', content: articleUrl },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: article.title || 'Gravaa - News Article Title' },
      { name: 'twitter:description', content: article.excerpt || 'Stay updated with the latest news and updates from Gravaa' },
      { name: 'twitter:image', content: articleImage },
    ],
    link: [
      { rel: 'canonical', href: articleUrl },
      { rel: 'icon', href: defaultLogo },
    ],
    script: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "NewsArticle",
          "headline": article.title || 'News - Gravaa',
          "description": article.excerpt || 'Stay updated with the latest news and updates from Gravaa',
          "image": [articleImage],
          "url": articleUrl,
          "datePublished": article.publishedAt || new Date().toISOString(),
          "author": {
            "@type": "Organization",
            "name": "Gravaa",
          },
        }),
      },
    ],
  });
};


// FAQ SEO & Schema Markup
export const useFaqSeo = (faqItems: Array<{ title: string; content: string; handle?: string }>) => {
  if (!faqItems?.length) return;

  const faqUrl = `${baseUrl}/faq`;

  // Era egna + defaults
  const defaultFaqItems = [ /* … som förut … */];
  const combinedFaqItems = [...faqItems, ...defaultFaqItems];

  useHead({
    title: 'Frequently Asked Questions – Gravaa',
    htmlAttrs: { lang: 'en' },
    meta: [
      { name: 'description', content: 'Find answers to the most common questions about Gravaa' },
      // … övriga meta …
    ],
    link: [
      { rel: 'canonical', href: faqUrl },
      // …
    ],
    script: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          "@context": "https://schema.org",
          // Sätt både WebPage och FAQPage på samma objekt
          "@type": ["WebPage", "FAQPage"],
          "@id": `${faqUrl}#page`,
          "url": faqUrl,
          "name": "Frequently Asked Questions – Gravaa",
          "description": "Find answers to the most common questions about Gravaa",
          "isPartOf": {
            "@type": "WebSite",
            "@id": `${baseUrl}#website`,
            "url": baseUrl,
            "name": "gravaa-shopify",
            "inLanguage": "en"
          },
          // Här kommer era frågor
          "mainEntity": combinedFaqItems.map(item => ({
            "@type": "Question",
            "name": item.title,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": item.content
            }
          }))
        })
      }
    ]
  });
};